import { createFileRoute, notFound, Outlet } from '@tanstack/react-router';

import { changeLocale, isLocale } from '../../lib/i18n';

/**
 * The locale segment. Spec 4.2: "Locale is a path prefix: /:lang/... with
 * lang ∈ {ru, uk, de, en}."
 *
 * An unknown language is a 404 rather than a silent fallback to English: `/fr/pricing`
 * returning an English page would be indexed as a French URL serving English content.
 */
export const Route = createFileRoute('/$lang')({
  beforeLoad: ({ params }) => {
    if (!isLocale(params.lang)) throw notFound();
  },

  /*
   * The locale bundle is loaded HERE, in the route loader, rather than in an effect in the
   * component below — which is where it used to be, and which stopped working when the
   * bundles became lazy (spec 23.2).
   *
   * An effect runs after the first paint. With resources already in memory that was
   * invisible; with an `import()` in the way it would mean every navigation into a
   * non-English locale rendering a frame of English first and then swapping. The router
   * already has a pending state for exactly this (`defaultPendingMs: 200` in router.tsx),
   * so awaiting here costs a spinner on a slow connection and nothing on a fast one.
   *
   * `changeLocale` swallows a failed load and leaves `fallbackLng: 'en'` to cover it, so
   * this never rejects and never blocks the route.
   */
  loader: async ({ params }) => {
    if (isLocale(params.lang)) await changeLocale(params.lang);
  },

  component: LocaleLayout,
});

function LocaleLayout(): React.ReactNode {
  return <Outlet />;
}
